import React, { useCallback, useState } from 'react';
import classNames from 'classnames';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { patchAssembly } from 'containers/KitWorkspaceStorage/api';
import { buildSpecSummary } from 'common/components/kit-shared/assemblySummary';
import type { IAssembly } from 'containers/KitWorkspaceStorage/types';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import styles from './AssemblyFormDialog.module.scss';

const NAME_MAX = 60;
const NOTES_MAX = 240;

interface IAssemblyFormDialogProps {
  assembly: IAssembly;
  onCancel: () => void;
  onSaved: () => void;
}

/**
 * "Edit assembly" popup: name + notes only (hose/fittings are immutable here —
 * users copy and rebuild to change parts). Name is optional but recommended;
 * the field is highlighted while empty. Closes itself after a successful save.
 */
const AssemblyFormDialog = ({ assembly, onCancel, onSaved }: IAssemblyFormDialogProps) => {
  const t = useKitTranslate();
  const { refetch } = useKitWorkspace();
  const [name, setName] = useState(assembly.name ?? '');
  const [notes, setNotes] = useState(assembly.notes ?? '');
  const [busy, setBusy] = useState(false);

  const spec = buildSpecSummary(assembly);

  const onSubmit = useCallback(async () => {
    setBusy(true);
    try {
      await patchAssembly(assembly.uuid, { name: name.trim(), notes: notes.trim() });
      showToast(ToastTypes.success, t('texts.kits.toast.assemblyUpdated'));
      await refetch();
      onSaved();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
      setBusy(false);
    }
  }, [assembly.uuid, name, notes, refetch, onSaved, t]);

  return (
    <BottomSheet
      title={t('texts.kits.editAssembly.title')}
      subtitle={spec || undefined}
      onClose={onCancel}
      centered
      closeDisabled={busy}
      footer={(
        <>
          <Button
            schema={ButtonSchema.secondary}
            size={ButtonSize.small}
            onClick={onCancel}
            disabled={busy}
          >
            {t('texts.kits.catalog.cancel')}
          </Button>
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.small}
            onClick={onSubmit}
            disabled={busy}
          >
            {t('texts.kits.editAssembly.saveBtn')}
          </Button>
        </>
      )}
    >
      <div className={styles.field}>
        <label className={styles.label} htmlFor="assembly-form-name">
          {t('texts.kits.editAssembly.nameLabel')}
          {' '}
          {!name.trim() && (
            <span className={styles.recommended}>{t('texts.kits.recommended')}</span>
          )}
        </label>
        <input
          id="assembly-form-name"
          className={classNames(styles.input, { [styles.inputRecommended]: !name.trim() })}
          value={name}
          maxLength={NAME_MAX}
          placeholder={t('texts.kits.editAssembly.namePlaceholder')}
          onChange={(e) => setName(e.currentTarget.value)}
          disabled={busy}
        />
        <div className={styles.help}>{t('texts.kits.editAssembly.nameHelp')}</div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="assembly-form-notes">
          {t('texts.kits.editAssembly.notesLabel')}
          {' '}
          <span className={styles.optional}>{t('texts.kits.editAssembly.notesOptional')}</span>
        </label>
        <textarea
          id="assembly-form-notes"
          className={styles.textarea}
          value={notes}
          maxLength={NOTES_MAX}
          placeholder={t('texts.kits.editAssembly.notesPlaceholder')}
          onChange={(e) => setNotes(e.currentTarget.value)}
          disabled={busy}
        />
      </div>
    </BottomSheet>
  );
};

export default AssemblyFormDialog;
